import { useState } from "react";

import { PasswordField } from "@/components/index/PasswordField";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { useAppContext } from "@/context/AppContext";
import { useLanguage } from "@/context/LanguageContext";
import { useSessionContext } from "@/context/SessionContext";
import { KeyIcon, UserIcon, UserPlusIcon } from "@heroicons/react/24/solid";

const loginInputClassName =
  "col-span-3 focus:outline-none focus-visible:outline-none ";

const LoginForm = () => {
  const { handleSubmit, doForgottenPassword, setDoForgottenPassword } =
    useSessionContext();
  const { isLoading, doLogin, setDoLogin, errorMessage } = useAppContext();
  const { language } = useLanguage();
  const copy = {
    cs: {
      resetTitle: "Obnova hesla",
      loginTitle: "Přihlášení",
      registerTitle: "Registrace",
      password: "Heslo",
      passwordRepeat: "Heslo znovu",
      loading: "Načítání",
      resetSubmit: "Obnovit heslo",
      loginSubmit: "Přihlásit se",
      registerSubmit: "Registrovat se",
      forgotten: "Zapomenuté heslo",
      emailPlaceholder: "vas@email.cz",
    },
    en: {
      resetTitle: "Password recovery",
      loginTitle: "Login",
      registerTitle: "Registration",
      password: "Password",
      passwordRepeat: "Password again",
      loading: "Loading",
      resetSubmit: "Reset password",
      loginSubmit: "Login",
      registerSubmit: "Register",
      forgotten: "Forgot password",
      emailPlaceholder: "your@email.com",
    },
  }[language];

  const [password, setPassword] = useState("");
  const [passwordRepeat, setPasswordRepeat] = useState("");

  return (
    <div className="herocard-root">
      <div className="herocard-body justify-items-center">
        <h2 className="herocard-title">
          {doForgottenPassword
            ? copy.resetTitle
            : doLogin
              ? copy.loginTitle
              : copy.registerTitle}
        </h2>

        <form className="w-full space-y-6" onSubmit={handleSubmit}>
          <div className="app-form-row">
            <Label htmlFor="email" className="text-right">
              Email
            </Label>
            <Input
              type="email"
              id="email"
              name="email"
              autoComplete="email"
              autoFocus
              className={loginInputClassName}
              placeholder={copy.emailPlaceholder}
              required
            />
          </div>

          {!doForgottenPassword && (
            <PasswordField
              id="password"
              name="password"
              label={copy.password}
              value={password}
              onChange={setPassword}
              autoComplete={!doLogin ? "new-password" : "current-password"}
              inputClassName={loginInputClassName}
            />
          )}

          {!doLogin && !doForgottenPassword && (
            <PasswordField
              id="password-repeat"
              name="password-repeat"
              label={copy.passwordRepeat}
              value={passwordRepeat}
              onChange={setPasswordRepeat}
              autoComplete="new-password"
              inputClassName={loginInputClassName}
            />
          )}

          {errorMessage && (
            <div className="herocard-error">
              <p className="herocard-error-text">{errorMessage}</p>
            </div>
          )}

          <div className="flex justify-center">
            <Button
              type="submit"
              size="lg"
              disabled={isLoading}
              className="relative"
            >
              {isLoading ? (
                <>
                  <span className="opacity-0">{copy.loading}</span>
                  <span className="dots">
                    <span className="dot" />
                    <span className="dot" />
                    <span className="dot" />
                  </span>
                </>
              ) : doForgottenPassword ? (
                copy.resetSubmit
              ) : doLogin ? (
                copy.loginSubmit
              ) : (
                copy.registerSubmit
              )}
              <span className="absolute inset-0 pointer-events-none" />
            </Button>
          </div>
        </form>
      </div>

      <div className="herocard-nav">
        <Button
          variant="outline"
          onClick={() => setDoForgottenPassword(true)}
          className="w-full min-w-0 cursor-pointer"
        >
          <KeyIcon className="size-4 flex-none" />
          {copy.forgotten}
        </Button>
        <Button
          variant="outline"
          onClick={() => {
            setDoLogin(!doLogin);
            setDoForgottenPassword(false);
          }}
          className="w-full cursor-pointer"
        >
          {doLogin ? (
            <>
              <UserPlusIcon className="size-4 flex-none" />
              {copy.registerTitle}
            </>
          ) : (
            <>
              <UserIcon className="size-4 flex-none" />
              {copy.loginTitle}
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default LoginForm;
